import { Link as RouterLink } from 'react-router-dom';
import { Box, Container, Grid, Typography, alpha, Stack, Button } from '@mui/material';
import { motion } from 'framer-motion';
import { GiftOutlined, WalletOutlined, UnlockOutlined, ArrowRightOutlined } from '@ant-design/icons';
import AnimateButton from 'components/@extended/AnimateButton';
import { PageUrl } from 'const/page-url';
import { SECTION_PY } from 'const/section';

// ==============================|| PRICING SECTION ||============================== //
//
// 料金の専用セクション。金額そのものより、無料トライアル・初期費用なし・
// いつでも解約という「始めやすさ / やめやすさ」を並べて、検討のハードルを下げる構成。

const primaryColor = '#2aa1af';

const MONTHLY_PRICE = '7,700';
/** 月額 × 12か月 */
const ANNUAL_PRICE = '92,400';

const points = [
    {
        icon: <GiftOutlined />,
        title: '1ヶ月の無料トライアル',
        description: 'まずは1か月、費用をかけずに実際の業務でお試しいただけます。'
    },
    {
        icon: <WalletOutlined />,
        title: '初期費用なし',
        description: '導入時の初期費用や初期設定の費用は一切かかりません。'
    },
    {
        icon: <UnlockOutlined />,
        title: '解約はいつでも可能',
        description: '最低利用期間の縛りはありません。合わなければいつでも解約いただけます。'
    }
];

const PricingSection = () => (
    // 直前の FeatureOverviewSection・直後の SupportSection がどちらも白背景のため、
    // サイト共通の淡色(#f4f7f7)を敷いて別セクションだと分かるようにしている。
    <Box component="section" id="pricing" sx={{ py: SECTION_PY, bgcolor: '#f4f7f7', position: 'relative', overflow: 'hidden' }}>
        <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7, delay: 0.2 }}
                >
                    <Typography
                        variant="caption"
                        component="span"
                        sx={{ color: primaryColor, fontWeight: 900, letterSpacing: '0.2em', display: 'block', mb: 2 }}
                    >
                        PRICING
                    </Typography>
                    <Typography
                        variant="h2"
                        component="h2"
                        sx={{ fontWeight: 900, fontSize: { xs: '1.9rem', md: '2.6rem' }, color: '#1a1a1a', lineHeight: 1.4 }}
                    >
                        ご利用料金
                    </Typography>
                    {/*
                      * 料金体系をひと目で伝えるリード文。
                      * 「基本料金以外はかからない」ことが検討時にいちばん聞かれるため、
                      * 金額を出す前にここで言い切っている。
                      */}
                    <Typography
                        component="p"
                        sx={{
                            mt: 2.5,
                            color: '#4b5563',
                            fontSize: { xs: '0.92rem', md: '1rem' },
                            fontWeight: 600,
                            lineHeight: 1.9
                        }}
                    >
                        月額基本料金のみで、必要な機能がすべて使えます。
                        <Box component="br" />
                        初期費用・サポート費用はかかりません。
                    </Typography>
                </motion.div>
            </Box>

            {/* 価格 */}
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
            >
                <Box
                    sx={{
                        maxWidth: 720,
                        mx: 'auto',
                        p: { xs: 4, md: 5.5 },
                        borderRadius: '28px',
                        bgcolor: '#fff',
                        border: `2px solid ${primaryColor}`,
                        boxShadow: `0 22px 55px ${alpha(primaryColor, 0.15)}`,
                        textAlign: 'center'
                    }}
                >
                    <Typography
                        component="p"
                        sx={{ fontWeight: 800, fontSize: { xs: '0.95rem', md: '1.05rem' }, color: '#4b5563', mb: 2 }}
                    >
                        すべての機能が使えて
                    </Typography>

                    <Stack direction="row" alignItems="baseline" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
                        <Typography component="span" sx={{ fontWeight: 800, color: '#374151', fontSize: { xs: '1.05rem', md: '1.2rem' } }}>
                            月額
                        </Typography>
                        <Typography
                            component="span"
                            sx={{
                                fontWeight: 900,
                                fontSize: { xs: '3.4rem', md: '4.6rem' },
                                color: primaryColor,
                                letterSpacing: '-0.04em',
                                lineHeight: 1
                            }}
                        >
                            {MONTHLY_PRICE}
                        </Typography>
                        <Typography component="span" sx={{ fontWeight: 800, color: '#374151', fontSize: { xs: '1.05rem', md: '1.2rem' } }}>
                            円
                        </Typography>
                        <Typography component="span" sx={{ fontWeight: 700, color: '#8a9393', fontSize: { xs: '0.85rem', md: '0.95rem' } }}>
                            （税込）/ 教室
                        </Typography>
                    </Stack>
                </Box>
            </motion.div>

            {/* 3つの安心材料 */}
            <Grid container spacing={3} sx={{ mt: { xs: 1, md: 2 } }} alignItems="stretch">
                {points.map((point, index) => (
                    <Grid item xs={12} md={4} key={point.title}>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                            style={{ height: '100%' }}
                        >
                            <Box
                                component="article"
                                sx={{
                                    height: '100%',
                                    p: { xs: 3, md: 3.5 },
                                    borderRadius: '20px',
                                    bgcolor: '#fff',
                                    border: `1px solid ${alpha(primaryColor, 0.15)}`,
                                    boxShadow: `0 10px 28px ${alpha(primaryColor, 0.05)}`,
                                    textAlign: 'center'
                                }}
                            >
                                <Box
                                    aria-hidden="true"
                                    sx={{
                                        width: 52,
                                        height: 52,
                                        mx: 'auto',
                                        mb: 2,
                                        borderRadius: '16px',
                                        bgcolor: alpha(primaryColor, 0.09),
                                        color: primaryColor,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '1.4rem'
                                    }}
                                >
                                    {point.icon}
                                </Box>
                                <Typography
                                    variant="h3"
                                    component="h3"
                                    sx={{ fontWeight: 900, fontSize: { xs: '1.05rem', md: '1.12rem' }, color: '#1a1a1a', mb: 1.2 }}
                                >
                                    {point.title}
                                </Typography>
                                <Typography component="p" sx={{ color: '#666', fontSize: '0.88rem', lineHeight: 1.8 }}>
                                    {point.description}
                                </Typography>
                            </Box>
                        </motion.div>
                    </Grid>
                ))}
            </Grid>

            <Box sx={{ textAlign: 'center', mt: { xs: 5, md: 6 } }}>
                <AnimateButton>
                    <Button
                        component={RouterLink}
                        to={PageUrl.DocumentRequest.urlWithSlash}
                        size="large"
                        variant="contained"
                        endIcon={<ArrowRightOutlined aria-hidden="true" />}
                        sx={{
                            bgcolor: primaryColor,
                            px: 4.5,
                            py: 1.7,
                            borderRadius: '16px',
                            fontSize: '1.02rem',
                            fontWeight: 800,
                            boxShadow: `0 10px 24px ${alpha(primaryColor, 0.3)}`
                        }}
                    >
                        資料・お見積りを受け取る
                    </Button>
                </AnimateButton>
            </Box>
        </Container>
    </Box>
);

export default PricingSection;
